import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Navigation, Truck, ShoppingBag, CreditCard } from "lucide-react";
import { BUSINESS } from "../data/products";

const HIGHLIGHTS = [
  { icon: Truck, label: "Delivery" },
  { icon: ShoppingBag, label: "In-Store Pickup" },
  { icon: CreditCard, label: "Accepts Credit Cards" },
];

export const VisitUs = () => {
  return (
    <section
      id="visit"
      data-testid="visit-section"
      className="relative py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0b]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="text-sm uppercase tracking-[0.25em] text-zinc-500">
            Visit Us
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4">
            Stop by our <span className="text-gradient">Morrisville</span> store
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-3xl overflow-hidden border border-white/10 min-h-[420px]"
          >
            <iframe
              title="SMOK CITY location map - 411 West Trenton Avenue, Morrisville PA"
              data-testid="visit-map-embed"
              src={BUSINESS.mapsEmbed}
              className="w-full h-full min-h-[420px] map-dark"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-3xl p-8 md:p-10 flex flex-col"
          >
            <div className="flex items-start gap-4">
              <MapPin className="text-[#00e5ff] mt-1 shrink-0" size={22} />
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Address
                </div>
                <div className="text-white text-lg mt-1">
                  {BUSINESS.addressLine1}
                </div>
                <div className="text-zinc-400">{BUSINESS.addressLine2}</div>
              </div>
            </div>

            <div className="flex items-start gap-4 mt-7">
              <Phone className="text-[#00e5ff] mt-1 shrink-0" size={22} />
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Phone
                </div>
                <a
                  href={`tel:${BUSINESS.phone.replace(/[^0-9]/g, "")}`}
                  data-testid="visit-phone"
                  className="text-white text-lg mt-1 block hover:text-[#00e5ff] transition"
                >
                  {BUSINESS.phone}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 mt-7">
              <Clock className="text-[#00e5ff] mt-1 shrink-0" size={22} />
              <div className="w-full">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Business Hours
                </div>
                <div className="mt-2 space-y-2">
                  {BUSINESS.hours.map((h) => (
                    <div
                      key={h.day}
                      className="flex justify-between text-sm border-b border-white/5 pb-2"
                    >
                      <span className="text-zinc-300">{h.day}</span>
                      <span className="text-white">{h.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-7" data-testid="visit-highlights">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">
                Highlights
              </div>
              <div className="flex flex-wrap gap-2.5">
                {HIGHLIGHTS.map((h) => (
                  <span
                    key={h.label}
                    data-testid={`visit-highlight-${h.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="inline-flex items-center gap-2 text-sm px-3.5 py-2 rounded-full glass text-zinc-200"
                  >
                    <h.icon size={15} className="text-[#39ff14]" /> {h.label}
                  </span>
                ))}
              </div>
            </div>

            <a
              data-testid="visit-directions-button"
              href={BUSINESS.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#00e5ff] text-black font-semibold hover:shadow-[0_0_32px_rgba(0,229,255,0.5)] transition-all duration-300 hover:-translate-y-1"
            >
              <Navigation size={18} /> Get Directions
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
